// VIEWING one attachment from the Email session (busy-my-web#481, 2026-09-15 parity audit D-4).
//
// Figma section 40011253:261042 draws three frames of one screen — an image (40011255:261043), a
// file Busy cannot render (40011283:277466) and a text file (40011285:277882) — and they are the
// same screen three times:
//
//   middle  the file itself, centred, at whatever zoom and rotation the toolbar is set to
//   box     the crumb "Briefing ❯ 24 attachments ❯ ⟨Contactpage.jpeg ✕⟩", and under it a grey
//           toolbar row: the name · rotate · −  100%  + · ⤓ Download · 🗑
//   rail    "Attachment timeline", with one card describing the file
//
// This module is everything about that screen that is a RULE rather than a drawing: what the
// toolbar offers for a given file, what the zoom control does when it is pressed, and what the
// rail's card says. It is a plain value module like lib/emailSession.js — which is the file it is
// modelled on, down to `attachmentTimelineCard` standing where `sessionItem` stands: one function
// that turns "what is in hand" into the exact words of one rail card, testable with no store and
// nothing mounted.
//
// WHAT IT DELIBERATELY DOES NOT DECIDE: which renderer draws the bytes. That question was answered
// once, in `previewRendererFor` (lib/attachmentsBriefing.js), and asking it again here would be the
// fifth copy of `isPdfAttachment` that function was written to end.

import { previewRendererFor, attachmentSize } from '@/lib/attachmentsBriefing'
import { attachmentExtension } from '@/lib/emailAttachmentsListing'

/** Every word the viewer writes, in the frames' own spelling. */
export const VIEWER_TEXT = {
  close: 'Close',
  download: 'Download',
  // busy-my-web#538 (2026-09-21): the toolbar no longer carries a Remove/Delete control — Saffet's
  // report was that the viewer offered four ways to leave it, and the toolbar's delete-then-close
  // was one of the four. The word stays here because the row-action this moved to
  // (EmailAttachmentsList.vue's `attachment-delete`) still needs it; `viewerToolbar` below just
  // doesn't hand it out any more.
  remove: 'Delete',
  rotate: 'Rotate',
  zoomIn: 'Zoom in',
  zoomOut: 'Zoom out',
  // The rail's header. The frame writes it "Attchment timeline" — a typo in the Figma layer, not a
  // product decision (busy-my-web#481 spells it correctly in its own acceptance criteria), so the
  // word is spelled properly here and the frame is followed in everything else.
  railTitle: 'Attachment timeline',
  received: 'Attachment received',
  sent: 'Attachment sent',
  // "From" with no colon, "To:" with one — both exactly as the two frames draw them
  // (40011255:261043 and 40011285:277882). They are two different frames of one component and the
  // punctuation differs between them; copying that rather than tidying it is the rule for
  // everything else measured off these frames, so it is the rule here.
  from: 'From',
  to: 'To:',
}

// ── Zoom ────────────────────────────────────────────────────────────────────────────────────────
//
// The frame's control reads "−  100%  +", so 100 is the rest state and the steps are whole
// percentages. 25 per press: at 10 the button has to be pressed ten times to make a visible
// difference to a photograph, and at 50 the first press out of 100 is already half the picture gone.
// The bounds exist so the pair can never be pressed into a state the middle cannot draw — a 0%
// image is a blank screen that reads as a broken file.

export const ZOOM_REST = 100
export const ZOOM_STEP = 25
export const ZOOM_MIN = 25
export const ZOOM_MAX = 400

const clampZoom = (value) => {
  const n = Math.round(Number(value))
  if (!Number.isFinite(n)) return ZOOM_REST
  return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, n))
}

/** One press of "+" — and never past the ceiling. */
export function zoomIn(current) {
  return clampZoom(clampZoom(current) + ZOOM_STEP)
}

/** …and of "−". */
export function zoomOut(current) {
  return clampZoom(clampZoom(current) - ZOOM_STEP)
}

/** "100%" — what the control writes between the two buttons. */
export function zoomLabel(current) {
  return `${clampZoom(current)}%`
}

/** The CSS scale the middle applies. 1 at rest, so an untouched image is drawn at its own size. */
export function zoomScale(current) {
  return clampZoom(current) / 100
}

// ── Rotation ───────────────────────────────────────────────────────────────────────────────────
//
// One button, one direction, quarter turns: the frame draws a single circular-arrow glyph, not a
// pair, so four presses is the way back to where you started. Kept inside 0–359 so the value stored
// on the session stays readable and a long session cannot wander off into 3600°.

export function rotateRight(current) {
  const n = Number(current)
  return (((Number.isFinite(n) ? n : 0) + 90) % 360 + 360) % 360
}

/**
 * The file record the viewer holds, from a listing row, a Work-board pick or a message's
 * attachment — three surfaces that name the same fields three ways.
 *
 * Normalised HERE rather than at each of the three call sites so the store holds one shape and the
 * toolbar, the middle and the rail all read the same record. Null when there is no id: the bytes
 * are fetched by document id (`api.downloadDocument`), so a record without one is a viewer that
 * would open on nothing.
 *
 * @param {object} source a listing row / pick / attachment
 * @returns {object|null}
 */
function directionIsSent(source) {
  const direction = String(source?.direction || '').trim().toLowerCase()
  if (direction === 'sent') return true
  if (direction === 'received') return false
  return source?.sent === true
}

export function attachmentViewerFile(source) {
  const id = String(source?.id ?? source?.documentId ?? '').trim()
  if (!id) return null
  const name = String(source?.name || source?.fileName || source?.filename || '').trim() || 'Attachment'
  const octets = Number(source?.octets ?? source?.size) || 0
  return {
    id,
    name,
    contentType: String(source?.contentType || ''),
    octets,
    // The listing has already written the size as a person reads it; anything else gets it written
    // now, from the one formatter (`attachmentSize`) every file surface in the product shares.
    sizeLabel: typeof source?.size === 'string' && source.size ? source.size : attachmentSize(octets),
    timelineId: source?.timelineId ?? source?.related?.id ?? null,
    // WHO the file is with, and WHICH WAY it went. Both are what the OPENER knows, and the two
    // openers know different amounts: a message in the reader knows its own sender, while the
    // documents listing carries only `relatedEmail` (busy-main-bff#403 — the correspondent's NAME;
    // the payload has no address field at all and adding one is a BFF change this issue does not
    // make). So the card takes what it is given and defaults to RECEIVED, which is what an
    // attachment in a mailbox overwhelmingly is — see `attachmentTimelineCard`.
    // The ADDRESS wins over the display name when the row carries one (busy-main-bff#43,
    // 2026-09-15 parity audit): the frames draw an email address under the caption, and until #43
    // the documents listing had only `relatedEmail`, a NAME. Order matters — `senderAddress` first,
    // then whatever the opener already knew.
    correspondent: String(
      source?.senderAddress || source?.correspondent || source?.relatedEmail || source?.related?.label || ''
    ).trim(),
    // WHICH WAY it went. `direction` is busy-main-bff#43's own field ('received' | 'sent'); the
    // older `sent` boolean is still read, and with neither present the card stays RECEIVED, which
    // is what an attachment in a mailbox overwhelmingly is.
    sent: directionIsSent(source),
    receivedOn: source?.receivedOn ?? source?.lastModified ?? source?.updateOn ?? source?.createdOn ?? null,
  }
}

/** The renderer key for a viewer record — `previewRendererFor`'s answer, asked in its own words. */
export function viewerRenderer(file) {
  return previewRendererFor({ name: file?.name, contentType: file?.contentType })
}

/** Is this file one the middle draws as a PICTURE? Only those get the zoom and the rotate. */
export function viewerIsImage(file) {
  return viewerRenderer(file) === 'image'
}

/**
 * The toolbar row (frames 40011255:261043 vs 40011283:277466 / 40011285:277882).
 *
 * The image frame carries the rotate button and the "−  100%  +" pair; the .fig and .txt frames
 * carry neither, and write only "name · Download · 🗑". That is not a size difference — rotating a
 * text file and zooming a file that is not being drawn are controls with nothing behind them — so
 * the split is exactly "is it a picture".
 *
 * NO `remove` HERE (busy-my-web#538, 2026-09-21): the toolbar used to carry a 🗑 that deleted the
 * file and, as a side effect of the file it was drawing ceasing to exist, closed the viewer — a
 * fourth way to leave it, on top of the header's own ×, its "← Back" and the bottom-left "✕ Close".
 * Saffet's report asked for exactly two (the × and Close), so deleting a file moved to its own row
 * in the attachments listing (EmailAttachmentsList.vue's `attachment-delete`, beside that row's
 * existing View/Download/Open-the-email) rather than living inside a screen whose whole job is
 * looking at one file.
 *
 * @param {object} file a record from {@link attachmentViewerFile}
 * @returns {object|null}
 */
export function viewerToolbar(file) {
  if (!file?.id) return null
  return {
    name: file.name,
    zoom: viewerIsImage(file),
    rotate: viewerIsImage(file),
    download: { text: VIEWER_TEXT.download, ready: true },
  }
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/**
 * The rail card's stamp, as the frames write it: "Aug 27, 26" over "12:30 PM".
 *
 * Two-digit year and a 12-hour clock — deliberately NOT the listing's own "Aug 27, 2026 at 12:30"
 * (`attachmentDateAdded`). They are the same fact written for two different widths: the listing has
 * a 136px column and the rail has the right-hand third of a 296px panel, and the frames spell each
 * one for its own space.
 *
 * Both fields are '' for a date the server did not send, so the card simply has no stamp rather
 * than printing "Invalid Date".
 *
 * @param {string|number|Date|null} value
 * @returns {{date:string, time:string}}
 */
export function viewerStamp(value) {
  if (value == null || value === '') return { date: '', time: '' }
  // The BFF sends naked local-looking timestamps for these rows; the listing's own formatter makes
  // the same assumption (lib/emailAttachmentsListing's `attachmentDateAdded`) and the two must not
  // disagree about what o'clock a file arrived.
  const raw = typeof value === 'string' && !value.endsWith('Z') && !value.includes('+') ? `${value}Z` : value
  const date = raw instanceof Date ? raw : new Date(raw)
  if (isNaN(date.getTime())) return { date: '', time: '' }
  const hours = date.getHours()
  const half = hours >= 12 ? 'PM' : 'AM'
  const twelve = hours % 12 === 0 ? 12 : hours % 12
  return {
    date: `${MONTHS[date.getMonth()]} ${date.getDate()}, ${String(date.getFullYear()).slice(-2)}`,
    time: `${twelve}:${String(date.getMinutes()).padStart(2, '0')} ${half}`,
  }
}

/** "JPEG" / "FIG" / "TXT" — the rail's last line, which is the extension and nothing more. */
export function viewerKind(file) {
  const ext = attachmentExtension(file?.name)
  if (ext) return ext.toUpperCase()
  const type = String(file?.contentType || '').split('/')[1] || ''
  return type ? type.toUpperCase() : 'FILE'
}

/**
 * THE RAIL'S ONE CARD (frames 40011255:261043 and 40011285:277882).
 *
 *   ⊘  Contactpage.jpeg                 Aug 27, 26
 *      Attachment received               12:30 PM
 *      From
 *      ✉ saffet@example.com
 *      ⛁ 10 KB
 *      ▤ JPEG
 *
 * Shaped here for the same reason `sessionItem` is shaped in lib/emailSession.js: the rail is one
 * of three surfaces describing one file, and a card assembled inside the component is a card no
 * test can ask a question of.
 *
 * DIRECTION is the one thing the card cannot always know. `sent` comes from the opener — a message
 * in the reader knows whose message it is — and the attachments LISTING does not carry it, because
 * busy-main-bff's documents payload has no sender address on it (busy-main-bff#403 added the
 * correspondent's display name and nothing else). Unknown therefore reads as RECEIVED, which is
 * what an attachment in a mailbox overwhelmingly is, and the frames' "Attachment sent / To:" half
 * is reached from the reader where the truth is in hand. Recorded rather than faked.
 *
 * @param {object} file a record from {@link attachmentViewerFile}
 * @returns {object|null} null when there is no file open, so the rail draws nothing
 */
export function attachmentTimelineCard(file) {
  if (!file?.id) return null
  const stamp = viewerStamp(file.receivedOn)
  return {
    id: file.id,
    title: file.name,
    event: file.sent ? VIEWER_TEXT.sent : VIEWER_TEXT.received,
    date: stamp.date,
    time: stamp.time,
    // The caption over the address — "From" for a file that arrived, "To:" for one that was sent.
    partyLabel: file.sent ? VIEWER_TEXT.to : VIEWER_TEXT.from,
    party: file.correspondent,
    size: file.sizeLabel,
    kind: viewerKind(file),
    // The icon set has no cloud glyph (the frame's size row wears one), so the three fact rows use
    // the nearest members of the pack the rest of the file surfaces already draw from.
    icons: { party: 'email', size: 'file-1', kind: 'file-doc' },
  }
}

export default attachmentViewerFile
